import React, { useEffect, useState } from 'react'
import { Box, Button } from '@mui/material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../../generic/systemValues/systemValueLabel'
import { GetAccumulatedUtcTime, GetUtcTime } from '../../generic/DateTimeFunctions'
import { BRANCH, collectJobNodes, computeJobTimeMs, computeSetSummary, resolveNodeLinks } from './processTree'
import { resolveJobMaterialRows } from './processTreeCoverage'
import ProcessTreeJobForm from './ProcessTreeJobForm'
import JobTaskTable from './JobTaskTable'
import ProcessTreeMaterialList from './ProcessTreeMaterialList'
import {
  DASH, T, TAB, TABULAR,
  EmptyCard, Field, Hint, LinkChip, LinksFoot, OperationCard, SectionTitle, SkippedMark, TabStrip, Tile, Trail
} from './ProcessTreeInspectorParts'
import { NIGHT_JOB_CONTEXT_OFF } from './jobNightCharacter'

/**
 * ProcessTreeInspector.jsx
 * The inspector on the right of the process Tree surface (story 118, RN67-RN69), transcribed from the MIC
 * Workflow Studio's "Editor del nodo" and painted with `themeConfig.js`.
 *
 * Since the PO review of 2026-09-10 it EDITS, and since the one of 2026-09-11 it edits EVERYTHING the
 * graph's job panel edits: with `canEdit` (which the view derives from `workflowData.editing`, the
 * permissions and the selection being a top-level job) the form writes through the SAME reducer actions
 * the graph uses — `UPDATE_JOB_REFERENCE`, `UPDATE_TASKS`, `UPDATE_NODE_NAME`, `UPDATE_NODE_CODE`,
 * `UPDATE_JOB_TECHNOLOGY` — and is persisted by the editor's own Save. Outside edit mode every control is
 * a div: nothing to type into (S83), and `editHint` says why.
 *
 * A NESTED job (inside a set) edits too: the view routes its writes to `UPDATE_NESTED_JOB` (by id, at any
 * depth) instead of the current-node actions, which for a nested selection would land on the containing
 * set (RN63). The RN63.1 hint still tells the reader that the graph selects the set, not the job.
 *
 * Three things this file must not get wrong:
 *  - RN67.1 — the Tasks counter is `taskReferences.length`. The job panel's tool counter
 *    (`NodeGeneralProperties.jsx:88`) is NOT reused: it produces NaN as soon as one task carries no tools.
 *  - §7.3 — a set is typed "Operation set", never "Set" (the verb "to put" in Spanish), and the time
 *    counter is "Duration", never "Time" (the clock hour).
 *  - RN51.1/POL2 — without the permission a block IS NOT DRAWN. Never drawn as zero, and never as an
 *    empty task list, which would state that there are no tasks.
 *
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Node} Node
 */

const labelOf = (candidate) => candidate.code || candidate.name || candidate.id

/*
 * `flex: '1 1 auto'` y NUNCA `flex: 1`. `flex: 1` es base CERO: dentro de un panel cuyo alto lo dicta su
 * contenido, un hijo con base 0 mide 0 y solo crece si sobra hueco — y el hueco se calcula a partir de
 * las bases. El panel se ajustaba a "cabecera y nada", y de la tabla de tareas se veia solo su cabecera
 * por muchas filas que tuviera (PO, 2026-09-23). Con base `auto` el contenido cuenta.
 */
const ProcessTreeInspector = ({
  node = null,
  /**
   * PO review 2026-09-22: UNA sola ventana de material. Una linea de material seleccionada en el arbol
   * NO abre un panel propio — se inspecciona la operacion que la consume, con la pestana Material
   * delante y esa linea marcada dentro de la lista que esa pestana ya tenia. Antes habia dos vistas de
   * material que ni se parecian, y desde la aislada no habia manera de llegar al job.
   */
  materialSelectedKey = '',
  onSelectMaterialLine = () => {},
  nested = false,
  levelNodes = [],
  branchIndex = 0,
  branchCount = 0,
  depth = 0,
  trail = [],
  coverage = null,
  showCounters = true,
  showTasks = true,
  showMaterial = true,
  canEdit = false,
  canEditTasks = false,
  editHint = '',
  requiredNodes = [],
  onUpdateJobReference = () => {},
  onUpdateTasks = () => {},
  onUpdateNodeName = () => {},
  onUpdateNodeCode = () => {},
  onUpdateTechnology = () => {},
  onSelectNode = () => {},
  branch = null,
  onSelectRowKey = () => {},
  progressCalculationTypes = [],
  indexValues = [],
  workflowUseIndex = false,
  // Story 208 (S31): SHOW_TASK_MACHINE_TIME, handed to the task table of the Tasks tab.
  showMachineTime = false,
  featureGroupRevisionControlled = false,
  featureGroupBaselines = {},
  cascadeApprovalEnabled = false,
  fabricationOrderOperable = true,
  nightJobContext = NIGHT_JOB_CONTEXT_OFF,
  // Story #150: what the extracted task table needs, threaded from ProcessTreeView.
  onManageResources = () => {},
  onEditingRowChanged = () => {},
  workflowName = '',
  partReference = null,
  getTempUUID = () => undefined,
  setTempUUID = () => {}
}) => {
  const { t_ } = useSmartTranslation()
  const [tab, setTab] = useState('')

  /**
   * Llegar pulsando una linea de material ABRE la pestana Material, pero no la clava: antes se imponia
   * en cada pintada y las pestanas General y Tareas quedaban muertas — se pulsaban y no pasaba nada.
   * Se lleva al usuario donde ha pedido ir, y a partir de ahi manda el.
   */
  useEffect(() => {
    if (materialSelectedKey) {
      setTab(TAB.MATERIAL)
    }
  }, [materialSelectedKey])

  /**
   * A BRANCH selected in the tree, inspected as a node: where it opens, where it closes, what it holds and
   * who runs beside it. Previous/Next are the opening and the closing operation — RN68 at branch level.
   */
  if (branch) {
    const jobs = branch.jobs || []
    const totalMs = jobs.reduce((total, jobNode) => total + computeJobTimeMs(jobNode.jobReference).ms, 0)
    const opensAt = branch.splitNode
      ? <LinkChip testId='inspector-branch-opens' label={labelOf(branch.splitNode)} branch={BRANCH.OK} onClick={() => onSelectNode(branch.splitNode.id)} />
      : <LinkChip testId='inspector-branch-opens' label={t_('Start of process')} branch={null} />
    const closesAt = branch.joinNode
      ? <LinkChip testId='inspector-branch-closes' label={labelOf(branch.joinNode)} branch={BRANCH.OK} onClick={() => onSelectNode(branch.joinNode.id)} />
      : <LinkChip testId='inspector-branch-closes' label={t_('End of process')} branch={null} />
    return (
      <Box data-testid='process-tree-inspector' data-mode='branch' sx={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: T.sans, color: T.textMain }}>
        <Box sx={{ padding: '14px 16px 0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Trail steps={trail} />
          <Box data-testid='inspector-type' sx={{ fontSize: '11px', fontWeight: 800, letterSpacing: '.16em', textTransform: 'uppercase', color: T.accent }}>{t_('Branch')}</Box>
          <Box data-testid='inspector-identity' sx={{ fontSize: '18px', fontWeight: 700, color: T.textMain, lineHeight: 1.2 }}>
            {t_('Branch {{index}} of {{count}}', { index: branch.index, count: branch.count })}
          </Box>
        </Box>
        <Box sx={{ borderBottom: '1px solid ' + T.borderSoft, marginTop: '12px' }} />
        <Box sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'hidden', padding: '14px 16px', minHeight: 0, display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {showCounters
            ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px' }}>
                <Tile testId='inspector-counter-operations' label={t_('Operations')} value={String(jobs.length)} />
                <Tile testId='inspector-counter-duration' label={t_('Duration')} value={totalMs > 0 ? GetAccumulatedUtcTime(totalMs) : DASH} accent />
              </Box>
              )
            : null}
          <Box>
            <SectionTitle label={t_('Opens at')} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>{opensAt}</Box>
          </Box>
          <Box>
            <SectionTitle label={t_('Closes at')} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>{closesAt}</Box>
          </Box>
          <Box>
            <SectionTitle label={t_('Operations')} />
            <Box data-testid='inspector-branch-operations' sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {jobs.length === 0
                ? <EmptyCard text={DASH} />
                : jobs.map((jobNode, index) => {
                  const time = computeJobTimeMs(jobNode.jobReference)
                  return (
                    <OperationCard
                      key={jobNode.id}
                      testId={'inspector-branch-operation-' + jobNode.id}
                      order={index + 1}
                      title={labelOf(jobNode)}
                      subtitle={systemValueLabel(t_, 'technology', jobNode.jobReference?.technology) || DASH}
                      figure={showCounters ? (time.isUnknown ? DASH : GetUtcTime(time.ms)) : ''}
                      onClick={() => onSelectNode(jobNode.id)}
                    />
                  )
                })}
            </Box>
          </Box>
          {(branch.siblings || []).length > 0
            ? (
              <Box>
                <SectionTitle label={t_('Sibling branches')} />
                <Box data-testid='inspector-branch-siblings' sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {branch.siblings.map((sibling) => (
                    <LinkChip key={sibling.key} testId={'inspector-branch-sibling-' + sibling.index} label={t_('Branch {{index}} of {{count}}', { index: sibling.index, count: branch.count })} branch={BRANCH.OK} onClick={() => onSelectRowKey(sibling.key)} />
                  ))}
                </Box>
              </Box>
              )
            : null}
        </Box>
        <LinksFoot previousLabel={t_('Previous')} nextLabel={t_('Next')} previous={opensAt} next={closesAt} />
      </Box>
    )
  }

  if (!node) {
    return (
      <Box data-testid='process-tree-inspector' sx={{ padding: '14px 16px', fontFamily: T.sans, fontSize: '13px', color: T.textFaint }}>
        {t_('Select an operation')}
      </Box>
    )
  }

  const isSet = node.nodeType === 'WORKFLOW'
  const jobReference = node.jobReference || {}
  const jobTime = isSet ? null : computeJobTimeMs(jobReference)
  const setSummary = isSet ? computeSetSummary(node) : null
  const tasks = jobReference.taskReferences || []
  const links = resolveNodeLinks(node.id, levelNodes)
  // The Material tab shows exactly the lines the tree draws under the row, so the two can never disagree.
  const materialRows = !isSet && coverage ? resolveJobMaterialRows(node, coverage) : []
  /**
   * What a set holds: the SAME list `computeSetSummary` counts — every job at any depth, deduplicated —
   * so the list and the Operations figure can never disagree. An unhydrated set counts nothing and
   * therefore lists nothing (POL2: no zeros, no empty list pretending to be an answer).
   */
  const setJobs = isSet && setSummary.hydrated ? collectJobNodes(node.subWorkflow?.nodes) : []

  const durationValue = isSet
    ? (setSummary.hydrated && setSummary.timeMs > 0 ? GetAccumulatedUtcTime(setSummary.timeMs) : DASH)
    : (jobTime.isUnknown ? DASH : GetUtcTime(jobTime.ms))

  const operators = jobReference.operatorQuantity ?? 0
  const minimumQuantity = jobReference.minimumQuantity || 1
  const editJob = !isSet && canEdit
  const editTasks = !isSet && canEditTasks

  const tabs = [
    { value: TAB.GENERAL, label: t_('General') },
    ...(showTasks && !isSet ? [{ value: TAB.TASKS, label: t_('Tasks') }] : []),
    ...(showMaterial && !isSet ? [{ value: TAB.MATERIAL, label: t_('Material') }] : [])
  ]
  const activeTab = tabs.some((entry) => entry.value === tab) ? tab : TAB.GENERAL

  return (
    <Box
      data-testid='process-tree-inspector'
      data-editable={editJob}
      sx={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: T.sans, color: T.textMain }}
    >
      {/* ═══ Node header — trail, type, name; the only mark left is "skipped" ═══ */}
      <Box sx={{ padding: '14px 16px 0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Trail steps={trail} />
        <Box data-testid='inspector-type' sx={{ fontSize: '11px', fontWeight: 800, letterSpacing: '.16em', textTransform: 'uppercase', color: T.accent }}>
          {isSet ? t_('Operation set') : t_('Operation')}
        </Box>
        <Box data-testid='inspector-identity' sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flexWrap: 'wrap' }}>
          <Box component='span' sx={{ flexGrow: 1, minWidth: 0, fontSize: '18px', fontWeight: 700, color: T.textMain, lineHeight: 1.2, overflowWrap: 'anywhere' }}>
            {node.name || node.code || ''}
          </Box>
          {node.name && node.code
            ? <Box component='span' sx={{ flex: '0 0 auto', fontSize: '12.5px', ...TABULAR, color: T.textFaint, whiteSpace: 'nowrap' }}>{node.code}</Box>
            : null}
          {node.skipped ? <SkippedMark label={t_('Skipped')} /> : null}
        </Box>
      </Box>

      <TabStrip tabs={tabs} active={activeTab} onChange={setTab} />

      {/*
        * ═══ Active tab content ═══
        *
        * PO review 2026-09-23: desplaza en los DOS ejes. Recortaba en horizontal, y la tabla de tareas
        * —la misma del grafo, ancha— se cortaba sin manera de llegar al resto.
        *
        * La barra va aqui y no en una caja alrededor de la tabla, y no por gusto: `overflow-x: auto` con
        * `overflow-y: visible` NO EXISTE en CSS — poner uno distinto de `visible` computa el otro a
        * `auto`. Una caja "solo horizontal" alrededor de la tabla se convertia en contenedor de scroll
        * vertical tambien, se comia el alto del padre y dejaba a las filas sin un pixel: toolbar,
        * busquedas, cabecera y paginacion, y ni una fila. Aqui el contenedor ya desplazaba en vertical,
        * asi que anadir el horizontal no cambia el alto de nada.
        *
        * La cabecera del panel y el pie de enlaces son HERMANOS de esta caja, no van dentro: la barra
        * horizontal mueve el contenido de la pestana y a ellos no los toca.
        */}
      <Box data-testid='inspector-tab-body' sx={{ flexGrow: 1, overflowY: 'auto', overflowX: 'auto', padding: '14px 16px', minHeight: 0 }}>
        {/* RN63.1: the asymmetry with the graph is declared — and why the node cannot be edited here. */}
        {nested ? <Hint testId='inspector-nested-hint' text={t_('Nested operation — the graph selects its containing set')} /> : null}
        {!isSet && !nested && !canEdit && editHint !== '' ? <Hint testId='inspector-edit-hint' text={editHint} /> : null}

        {activeTab === TAB.GENERAL
          ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* RN51.1/POL2: without WORKFLOW_JOBS the tiles ARE NOT DRAWN, never as zeros. */}
              {showCounters
                ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(' + (isSet ? 2 : 4) + ', minmax(0, 1fr))', gap: '8px' }}>
                    {isSet
                      ? <Tile testId='inspector-counter-operations' label={t_('Operations')} value={String(setSummary.jobCount)} />
                      : <Tile testId='inspector-counter-tasks' label={t_('Tasks')} value={String(tasks.length)} />}
                    <Tile testId='inspector-counter-duration' label={t_('Duration')} value={durationValue} accent />
                    {isSet ? null : <Tile testId='inspector-counter-operators' label={t_('Operators')} value={String(operators)} />}
                    {isSet ? null : <Tile testId='inspector-counter-quantity' label={t_('Minimum quantity')} value={String(minimumQuantity)} />}
                  </Box>
                  )
                : null}

              {/* PO review 2026-09-11: what has to be finished before this one — every OK predecessor, at any distance. */}
              {requiredNodes.length > 0
                ? (
                  <Box>
                    <SectionTitle label={t_('Depends on')} />
                    <Box data-testid='inspector-required' sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {requiredNodes.map((required) => (
                        <LinkChip key={required.nodeId} testId={'inspector-required-' + required.nodeId} label={required.label} branch={BRANCH.OK} onClick={() => onSelectNode(required.nodeId)} />
                      ))}
                    </Box>
                  </Box>
                  )
                : null}

              {isSet
                ? (
                  <>
                    <Box>
                      <SectionTitle label={t_('Operations')} />
                      <Box data-testid='inspector-set-operations' sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {setJobs.length === 0
                          ? <EmptyCard text={DASH} />
                          : setJobs.map((jobNode, index) => {
                            const time = computeJobTimeMs(jobNode.jobReference)
                            return (
                              <OperationCard
                                key={jobNode.id}
                                testId={'inspector-set-operation-' + jobNode.id}
                                order={index + 1}
                                title={jobNode.code || jobNode.name || ''}
                                subtitle={systemValueLabel(t_, 'technology', jobNode.jobReference?.technology) || DASH}
                                figure={showCounters ? (time.isUnknown ? DASH : GetUtcTime(time.ms)) : ''}
                                onClick={() => onSelectNode(jobNode.id)}
                              />
                            )
                          })}
                      </Box>
                    </Box>
                    <Box>
                      <SectionTitle label={t_('Identity')} />
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px 14px' }}>
                        <Field testId='inspector-field-name' label={t_('Step name')} value={node.name || DASH} full />
                        <Field testId='inspector-field-code' label={t_('Code in the process')} value={node.code || DASH} />
                        <Field testId='inspector-field-level' label={t_('Sequence level')} value={String(depth + 1)} />
                        {branchIndex > 0 && branchCount > 0
                          ? <Field testId='inspector-branch' label={t_('Branch')} value={t_('Branch {{index}} of {{count}}', { index: branchIndex, count: branchCount })} />
                          : null}
                      </Box>
                    </Box>
                  </>
                  )
                : (
                  <ProcessTreeJobForm
                    idPrefix='inspector'
                    node={node}
                    jobReference={jobReference}
                    depth={depth}
                    branchIndex={branchIndex}
                    branchCount={branchCount}
                    editable={editJob}
                    showCounters={showCounters}
                    durationValue={durationValue}
                    onChange={onUpdateJobReference}
                    onChangeNodeName={(name) => onUpdateNodeName(node.id, name)}
                    onChangeNodeCode={(code) => onUpdateNodeCode(node.id, code)}
                    onChangeTechnology={(technology) => onUpdateTechnology(node.id, technology)}
                    progressCalculationTypes={progressCalculationTypes}
                    indexValues={indexValues}
                    workflowUseIndex={workflowUseIndex}
                    cascadeApprovalEnabled={cascadeApprovalEnabled}
                    featureGroupRevisionControlled={featureGroupRevisionControlled}
                    fabricationOrderOperable={fabricationOrderOperable}
                    nightJobContext={nightJobContext}
                  />
                  )}
            </Box>
            )
          : null}

        {activeTab === TAB.TASKS
          ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <SectionTitle label={t_('Tasks')} />
              {/*
                * Story #150 (D16): THE SAME table the graph shows, not a card list of its own. The list
                * that used to live here had drifted out of parity — no add, no delete, no reorder, no
                * programs, and a tool chip that read "No tool" for every task.
                * RN117.3.1: it reports its open row under its OWN host id, so a row left open here
                * blocks the save just as one left open in the graph does, and closing one does not
                * clear the other.
                */}
              <Box data-testid='inspector-task-list'>
                <JobTaskTable
                  // El panel es la superficie estrecha de las tres que montan esta tabla: aqui va compacta.
                  compact
                  node={node}
                  editing={editTasks}
                  updateTaskList={onUpdateTasks}
                  onEditingRowChanged={(isOpen) => onEditingRowChanged('tree-inspector-tasks', isOpen)}
                  workflowName={workflowName}
                  partReference={partReference}
                  getTempUUID={getTempUUID}
                  setTempUUID={setTempUUID}
                  indexValues={indexValues}
                  workflowUseIndex={workflowUseIndex}
                  showMachineTime={showMachineTime}
                  featureGroupRevisionControlled={featureGroupRevisionControlled}
                  featureGroupBaselines={featureGroupBaselines}
                />
              </Box>
            </Box>
            )
          : null}

        {activeTab === TAB.MATERIAL
          ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <SectionTitle label={t_('Resources')} />
              {/*
                * RN134.1 (story #150): a nested job's material is shown whole but is not edited here —
                * it belongs to its own process. Saying so is the difference between a read-only panel
                * and one that looks broken (POL105).
                */}
              {nested ? <Hint testId='inspector-material-nested-hint' text={t_('Nested operation — its material is edited from its own subworkflow')} /> : null}
              <ProcessTreeMaterialList
                idPrefix='inspector'
                loaded={!!coverage}
                rows={materialRows}
                selectedKey={materialSelectedKey}
                onSelect={onSelectMaterialLine}
              />
              {/*
                * RN146 (story #150): the full resources panel is two panels side by side and does not
                * fit a dock. The compact list stays here, and this is the way over to the window that
                * does host it — offered read-only too, so the way in never depends on edit rights.
                */}
              <Button
                variant='outlined'
                data-testid='inspector-manage-resources'
                onClick={onManageResources}
                sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
              >
                {t_('Manage resources')}
              </Button>
            </Box>
            )
          : null}
      </Box>

      {/* ═══ Foot — the process links ═══ */}
      <LinksFoot
        previousLabel={t_('Previous')}
        nextLabel={t_('Next')}
        previous={links.previous.map((link) => (link.start
          ? <LinkChip key='start' testId='inspector-chip-previous-start' label={t_('Start of process')} branch={null} />
          : <LinkChip key={'prev-' + link.nodeId + '-' + link.branch} testId={'inspector-chip-previous-' + link.nodeId} label={link.label || link.nodeId} branch={link.branch} onClick={() => onSelectNode(link.nodeId)} />))}
        next={links.next.map((link, index) => {
          if (link.end) {
            return <LinkChip key={'end-' + index} testId='inspector-chip-next-end' label={t_('End of process')} branch={link.branch} />
          }
          if (link.dangling) {
            return <LinkChip key={'dangling-' + index} testId='inspector-chip-next-dangling' label={t_('Unresolved link')} branch={link.branch} />
          }
          return <LinkChip key={'next-' + link.nodeId + '-' + index} testId={'inspector-chip-next-' + link.nodeId} label={link.label || link.nodeId} branch={link.branch} onClick={() => onSelectNode(link.nodeId)} />
        })}
      />
    </Box>
  )
}

export default ProcessTreeInspector
